import React, { useState } from "react";

import Button from "components/ui/Button";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const generateMonteCarlo = (iterations) => {
  const years = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  return years?.map((yr) => {
    const base = 485000 * Math.pow(1.042, yr);
    const spread = base * 0.18;
    return {
      year: `Yr ${yr}`,
      p10: Math.round(base - spread * 1.4),
      p25: Math.round(base - spread * 0.7),
      p50: Math.round(base),
      p75: Math.round(base + spread * 0.7),
      p90: Math.round(base + spread * 1.4),
    };
  });
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-xl px-4 py-3 shadow-lg">
      <p className="text-sm font-heading font-semibold text-foreground mb-2">{label}</p>
      {payload?.map((p) => (
        <div key={p?.dataKey} className="flex items-center justify-between gap-4 text-xs font-caption">
          <span style={{ color: p?.color }}>{p?.name}</span>
          <span className="font-mono-data font-semibold text-foreground">
            ${(p?.value / 1000)?.toFixed(0)}K
          </span>
        </div>
      ))}
    </div>
  );
};

export default function MonteCarloPanel() {
  const [iterations, setIterations] = useState(1000);
  const [running, setRunning] = useState(false);
  const [data, setData] = useState(() => generateMonteCarlo(1000));

  const runSimulation = async () => {
    setRunning(true);
    await new Promise((r) => setTimeout(r, 1200));
    setData(generateMonteCarlo(iterations));
    setRunning(false);
  };

  return (
    <div className="card-base flex flex-col gap-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-heading font-semibold text-base text-foreground">
            Monte Carlo Simulation
          </h3>
          <p className="text-xs font-caption text-muted-foreground mt-0.5">
            Property value probability distribution over 10 years
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-xs font-caption text-muted-foreground whitespace-nowrap">
              Iterations:
            </label>
            <select
              value={iterations}
              onChange={(e) => setIterations(Number(e?.target?.value))}
              className="h-8 px-2 text-xs font-caption bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value={500}>500</option>
              <option value={1000}>1,000</option>
              <option value={5000}>5,000</option>
              <option value={10000}>10,000</option>
            </select>
          </div>
          <Button
            variant="default"
            size="sm"
            iconName={running ? "Loader2" : "Play"}
            iconPosition="left"
            iconSize={14}
            loading={running}
            onClick={runSimulation}
          >
            {running ? "Running..." : "Run Simulation"}
          </Button>
        </div>
      </div>
      <div className="w-full h-64" aria-label="Monte Carlo Simulation Chart">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="p90grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.15} />
                <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="p50grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-secondary)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="var(--color-secondary)" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="year" tick={{ fontSize: 11, fill: "var(--color-muted-foreground)", fontFamily: "Inter" }} />
            <YAxis
              tick={{ fontSize: 11, fill: "var(--color-muted-foreground)", fontFamily: "JetBrains Mono" }}
              tickFormatter={(v) => `$${(v / 1000)?.toFixed(0)}K`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11, fontFamily: "Inter" }} />
            <Area type="monotone" dataKey="p90" name="90th Pct" stroke="var(--color-primary)" fill="url(#p90grad)" strokeWidth={1.5} strokeDasharray="4 2" />
            <Area type="monotone" dataKey="p75" name="75th Pct" stroke="var(--color-primary)" fill="transparent" strokeWidth={1} />
            <Area type="monotone" dataKey="p50" name="Median" stroke="var(--color-secondary)" fill="url(#p50grad)" strokeWidth={2.5} />
            <Area type="monotone" dataKey="p25" name="25th Pct" stroke="var(--color-warning)" fill="transparent" strokeWidth={1} />
            <Area type="monotone" dataKey="p10" name="10th Pct" stroke="var(--color-accent)" fill="transparent" strokeWidth={1.5} strokeDasharray="4 2" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 border-t border-border pt-4">
        {[
          { label: "Median Value (10yr)", value: "$731K", color: "var(--color-secondary)" },
          { label: "Best Case (90th)", value: "$892K", color: "var(--color-primary)" },
          { label: "Worst Case (10th)", value: "$571K", color: "var(--color-accent)" },
          { label: "Probability > Target", value: "78.4%", color: "var(--color-success)" },
        ]?.map((stat) => (
          <div key={stat?.label} className="flex flex-col gap-1">
            <p className="text-xs font-caption text-muted-foreground">{stat?.label}</p>
            <p className="text-lg font-mono-data font-bold" style={{ color: stat?.color }}>
              {stat?.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}